/**
 * Hide empty third-party widget leftovers that fail Siteimprove:
 * - empty ul/ol (SIA-R68 / ARIA 1.2 required owned elements)
 * - empty .yotpo-review-title headings (SIA-R64 / ARIA 1.2 5.2.8)
 */
(() => {
  const ROOT_SELECTOR = '.yotpo-widget-instance, .yotpo';
  const TITLE_SELECTOR = '.yotpo-review-title';
  const LIST_MARK = 'data-empty-list-a11y';
  const HEADING_MARK = 'data-empty-heading-a11y';

  let scheduled = false;

  /**
   * @param {HTMLElement} element
   * @param {string} mark
   */
  const restore = (element, mark) => {
    element.hidden = false;
    element.removeAttribute('role');
    element.removeAttribute(mark);
  };

  /**
   * @param {HTMLElement} element
   * @param {string} mark
   */
  const hideEmpty = (element, mark) => {
    element.hidden = true;
    element.setAttribute('role', 'presentation');
    element.setAttribute(mark, '');
  };

  /**
   * Siteimprove still treats a list as empty when every item is hidden.
   * Check the item only — the list itself may already be `[hidden]` from this script.
   * @param {Element} element
   * @returns {boolean}
   */
  const isExposed = (element) => {
    if (!(element instanceof HTMLElement)) return false;
    if (element.hidden || element.getAttribute('aria-hidden') === 'true') return false;

    return true;
  };

  /**
   * @param {HTMLUListElement | HTMLOListElement} list
   */
  const syncList = (list) => {
    const hasItem = Array.from(list.children).some(
      (child) => child.matches('li, [role="listitem"]') && isExposed(child)
    );

    if (hasItem) {
      if (!list.hasAttribute(LIST_MARK)) return;

      restore(list, LIST_MARK);
      return;
    }

    hideEmpty(list, LIST_MARK);
  };

  /**
   * @param {HTMLElement} heading
   * @returns {boolean}
   */
  const headingHasName = (heading) => {
    const labelledBy = heading.getAttribute('aria-labelledby');

    if (labelledBy) {
      for (const id of labelledBy.trim().split(/\s+/)) {
        const source = document.getElementById(id);

        if (source?.textContent?.trim()) return true;
      }
    }

    if (heading.getAttribute('aria-label')?.trim()) return true;
    if (heading.textContent.trim()) return true;

    for (const img of heading.querySelectorAll('img')) {
      if (img.getAttribute('alt')?.trim()) return true;
    }

    return false;
  };

  /**
   * @param {HTMLElement} heading
   */
  const syncHeading = (heading) => {
    if (headingHasName(heading)) {
      if (!heading.hasAttribute(HEADING_MARK)) return;

      restore(heading, HEADING_MARK);
      return;
    }

    hideEmpty(heading, HEADING_MARK);
  };

  const scan = () => {
    scheduled = false;

    for (const root of document.querySelectorAll(ROOT_SELECTOR)) {
      for (const list of root.querySelectorAll('ul, ol')) {
        if (list instanceof HTMLUListElement || list instanceof HTMLOListElement) {
          syncList(list);
        }
      }

      for (const heading of root.querySelectorAll(TITLE_SELECTOR)) {
        if (heading instanceof HTMLElement) {
          syncHeading(heading);
        }
      }
    }
  };

  const scheduleScan = () => {
    if (scheduled) return;

    scheduled = true;
    requestAnimationFrame(scan);
  };

  const observer = new MutationObserver(scheduleScan);

  const start = () => {
    scan();
    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
